import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { FormField } from "~/components/ui/form-field";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/cn";
import { OtpInput } from "~/components/ui/otp-input";
import { useSignInController } from "~/lib/auth/use-sign-in-controller";
import type {
  SupportedFirstFactorStrategy,
  SupportedSecondFactorStrategy,
} from "~/lib/auth/use-sign-in-controller";
import { UserSigninSchema } from "~/lib/schemas/user-auth";
import type { UserSigninForm } from "~/lib/schemas/user-auth";

const EmailOnlySchema = z.object({
  identifier: z.string().email("Please enter a valid email address"),
});

type EmailOnlyForm = z.infer<typeof EmailOnlySchema>;

type EmailCodeViewState = "request" | "verify";

type SupportedSecondFactor = SupportedSecondFactorStrategy;

interface SignInFormProps {
  className?: string;
  defaultEmail?: string;
  onSuccess?: () => Promise<void> | void;
  emailLinkRedirectUrl?: string;
}

const EMAIL_CODE_RESEND_INTERVAL_MS = 60_000;
const EMAIL_CODE_LENGTH = 6;

const createEmptyEmailCode = () =>
  Array.from({ length: EMAIL_CODE_LENGTH }, () => "");

function deriveInitialStrategy(
  available: SupportedFirstFactorStrategy[],
  fallback: SupportedFirstFactorStrategy,
): SupportedFirstFactorStrategy {
  if (available.length === 0) {
    return fallback;
  }
  return available.includes(fallback) ? fallback : available[0];
}

export function SignInForm({
  className,
  defaultEmail,
  onSuccess,
  emailLinkRedirectUrl,
}: SignInFormProps) {
  const controller = useSignInController({ onSignInComplete: onSuccess });
  const [activeStrategy, setActiveStrategy] =
    useState<SupportedFirstFactorStrategy>(() =>
      deriveInitialStrategy(controller.availableFirstFactors, "password"),
    );
  const [emailCodeView, setEmailCodeView] =
    useState<EmailCodeViewState>("request");
  const [emailCodeValue, setEmailCodeValue] =
    useState<string[]>(createEmptyEmailCode);
  const [secondFactorStrategy, setSecondFactorStrategy] =
    useState<SupportedSecondFactor | null>(null);
  const [secondFactorCode, setSecondFactorCode] = useState("");

  const passwordForm = useForm<UserSigninForm>({
    resolver: zodResolver(UserSigninSchema),
    defaultValues: {
      email: defaultEmail ?? "",
      password: "",
    },
  });

  const emailOnlyForm = useForm<EmailOnlyForm>({
    resolver: zodResolver(EmailOnlySchema),
    defaultValues: {
      identifier: defaultEmail ?? "",
    },
  });

  const isProcessing = controller.state.stage === "submitting";

  useEffect(() => {
    if (!defaultEmail) {
      return;
    }
    passwordForm.setValue("email", defaultEmail);
    emailOnlyForm.setValue("identifier", defaultEmail);
  }, [defaultEmail, emailOnlyForm, passwordForm]);

  useEffect(() => {
    const normalizedAvailable = controller.availableFirstFactors;
    setActiveStrategy((previous) =>
      deriveInitialStrategy(normalizedAvailable, previous),
    );
  }, [controller.availableFirstFactors]);

  useEffect(() => {
    if (controller.state.stage !== "awaiting_email_code") {
      setEmailCodeView("request");
      setEmailCodeValue(createEmptyEmailCode());
    } else {
      setEmailCodeView("verify");
    }
  }, [controller.state.stage]);

  useEffect(() => {
    if (controller.state.stage === "awaiting_second_factor") {
      if (
        controller.state.requiredSecondFactor &&
        controller.availableSecondFactors.includes(
          controller.state.requiredSecondFactor,
        )
      ) {
        setSecondFactorStrategy(controller.state.requiredSecondFactor);
      } else if (controller.availableSecondFactors.length > 0) {
        setSecondFactorStrategy(controller.availableSecondFactors[0]);
      }
      setSecondFactorCode("");
    }
  }, [
    controller.availableSecondFactors,
    controller.state.requiredSecondFactor,
    controller.state.stage,
  ]);

  const emailCodeResendSeconds = useEmailCodeResendCountdown(
    controller.state.emailCode?.lastSentAt ?? null,
  );

  const showMagicLinkOption = useMemo(() => {
    if (controller.availableFirstFactors.includes("email_link")) {
      return true;
    }
    return false;
  }, [controller.availableFirstFactors]);

  const handlePasswordSubmit = passwordForm.handleSubmit(async (data) => {
    await controller.startPasswordSignIn({
      identifier: data.email,
      password: data.password,
    });
  });

  const handleEmailIdentifierSubmit = emailOnlyForm.handleSubmit(
    async (data) => {
      if (activeStrategy === "email_code") {
        await controller.startEmailCodeSignIn({ identifier: data.identifier });
        return;
      }
      const redirectUrl =
        emailLinkRedirectUrl ??
        (typeof window !== "undefined" ? window.location.href : "/");
      await controller.startEmailLinkSignIn({
        identifier: data.identifier,
        redirectUrl,
      });
    },
  );

  const renderPasswordTab = () => (
    <form onSubmit={handlePasswordSubmit} className="space-y-4">
      <FormField
        label="Email"
        error={passwordForm.formState.errors.email?.message ?? undefined}
      >
        <Input
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          {...passwordForm.register("email")}
          disabled={isProcessing}
        />
      </FormField>
      <FormField
        label="Password"
        error={passwordForm.formState.errors.password?.message ?? undefined}
      >
        <Input
          type="password"
          autoComplete="current-password"
          placeholder="Enter your password"
          {...passwordForm.register("password")}
          disabled={isProcessing}
        />
      </FormField>
      <Button type="submit" loading={isProcessing} className="w-full">
        {isProcessing ? "Signing in..." : "Sign in"}
      </Button>
    </form>
  );

  const renderEmailCodeTab = () => (
    <div className="space-y-4">
      {emailCodeView === "request" ? (
        <form onSubmit={handleEmailIdentifierSubmit} className="space-y-4">
          <FormField
            label="Email"
            error={
              emailOnlyForm.formState.errors.identifier?.message ?? undefined
            }
          >
            <Input
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              {...emailOnlyForm.register("identifier")}
              disabled={isProcessing}
            />
          </FormField>
          <Button type="submit" loading={isProcessing} className="w-full">
            {isProcessing ? "Sending code..." : "Send verification code"}
          </Button>
          <p className="text-sm text-text-secondary">
            We'll send a six-digit code to your inbox.
          </p>
        </form>
      ) : (
        <div className="space-y-4">
          <FormField label="Verification code">
            <OtpInput
              value={emailCodeValue}
              length={EMAIL_CODE_LENGTH}
              onChange={(value) => setEmailCodeValue([...value])}
              onComplete={async (value) => {
                await controller.verifyEmailCode({ code: value });
              }}
              disabled={isProcessing}
            />
          </FormField>
          <div className="flex items-center justify-between text-sm text-text-secondary">
            <span>Didn't get a code?</span>
            <Button
              type="button"
              variant="link"
              className="px-0"
              disabled={isProcessing || emailCodeResendSeconds > 0}
              onClick={() => controller.resendEmailCode()}
            >
              {emailCodeResendSeconds > 0
                ? `Resend in ${emailCodeResendSeconds}s`
                : "Resend code"}
            </Button>
          </div>
          <Button
            type="button"
            className="w-full"
            loading={isProcessing}
            onClick={async () => {
              await controller.verifyEmailCode({
                code: emailCodeValue.join(""),
              });
            }}
            disabled={
              isProcessing ||
              emailCodeValue.some((digit) => digit.trim().length === 0)
            }
          >
            {isProcessing ? "Verifying..." : "Verify and continue"}
          </Button>
        </div>
      )}
    </div>
  );

  const renderEmailLinkTab = () => (
    <div className="space-y-4">
      {controller.state.stage !== "awaiting_email_link" ? (
        <form onSubmit={handleEmailIdentifierSubmit} className="space-y-4">
          <FormField
            label="Email"
            error={
              emailOnlyForm.formState.errors.identifier?.message ?? undefined
            }
          >
            <Input
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              {...emailOnlyForm.register("identifier")}
              disabled={isProcessing}
            />
          </FormField>
          <Button type="submit" loading={isProcessing} className="w-full">
            {isProcessing ? "Sending magic link..." : "Send magic link"}
          </Button>
          <p className="text-sm text-text-secondary">
            We'll email you a secure link to sign in instantly.
          </p>
        </form>
      ) : (
        <div className="space-y-4 text-center">
          <p className="text-sm text-text-secondary">
            We've sent a magic link to{" "}
            <strong>{emailOnlyForm.getValues("identifier")}</strong>. Open it on
            this device to finish signing in.
          </p>
          {controller.state.emailLink?.emailLinkUrl && (
            <Button
              type="button"
              className="w-full"
              onClick={() => {
                if (typeof window === "undefined") {
                  return;
                }
                window.open(
                  controller.state.emailLink?.emailLinkUrl ?? "",
                  "_blank",
                );
              }}
            >
              Open magic link
            </Button>
          )}
        </div>
      )}
    </div>
  );

  const renderSecondFactorStep = () => (
    <div className="space-y-4">
      <p className="text-sm text-text-secondary">
        Your account requires a second factor. Enter the code from your
        authenticator app or backup codes.
      </p>
      {controller.availableSecondFactors.length > 1 && (
        <div className="flex gap-2">
          {controller.availableSecondFactors.map((factor) => (
            <Button
              key={factor}
              type="button"
              variant={factor === secondFactorStrategy ? "default" : "outline"}
              onClick={() => {
                setSecondFactorStrategy(factor);
                setSecondFactorCode("");
              }}
            >
              {factor === "totp" ? "Authenticator app" : "Backup code"}
            </Button>
          ))}
        </div>
      )}
      <FormField
        label={
          secondFactorStrategy === "backup_code"
            ? "Backup code"
            : "Authenticator code"
        }
      >
        <Input
          value={secondFactorCode}
          onChange={(event) => setSecondFactorCode(event.target.value)}
          autoComplete="one-time-code"
          inputMode="numeric"
          pattern={
            secondFactorStrategy === "backup_code" ? undefined : "[0-9]*"
          }
          placeholder={
            secondFactorStrategy === "backup_code"
              ? "Enter backup code"
              : "123456"
          }
          disabled={isProcessing}
        />
      </FormField>
      <Button
        type="button"
        className="w-full"
        disabled={
          !secondFactorStrategy ||
          secondFactorCode.trim().length === 0 ||
          isProcessing
        }
        loading={isProcessing}
        onClick={async () => {
          if (!secondFactorStrategy) {
            return;
          }
          await controller.verifySecondFactor({
            strategy: secondFactorStrategy,
            code: secondFactorCode.trim(),
          });
        }}
      >
        {isProcessing ? "Verifying..." : "Verify"}
      </Button>
    </div>
  );

  const renderContent = () => {
    if (controller.state.stage === "awaiting_second_factor") {
      return renderSecondFactorStep();
    }

    return (
      <Tabs
        value={activeStrategy}
        onValueChange={(value) =>
          setActiveStrategy(value as SupportedFirstFactorStrategy)
        }
        className="space-y-4"
      >
        <TabsList
          className={cn(
            "grid gap-2",
            showMagicLinkOption ? "grid-cols-3" : "grid-cols-2",
          )}
        >
          <TabsTrigger value="password">Password</TabsTrigger>
          <TabsTrigger value="email_code">Email code</TabsTrigger>
          {showMagicLinkOption && (
            <TabsTrigger value="email_link">Magic link</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="password">{renderPasswordTab()}</TabsContent>
        <TabsContent value="email_code">{renderEmailCodeTab()}</TabsContent>
        {showMagicLinkOption && (
          <TabsContent value="email_link">{renderEmailLinkTab()}</TabsContent>
        )}
      </Tabs>
    );
  };

  return (
    <div className={className}>
      {renderContent()}
      {controller.state.error && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {controller.state.error}
        </div>
      )}
    </div>
  );
}

function useEmailCodeResendCountdown(lastSentAt: number | null): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1_000);

    return () => window.clearInterval(interval);
  }, []);

  if (!lastSentAt) {
    return 0;
  }

  const elapsed = now - lastSentAt;
  if (elapsed >= EMAIL_CODE_RESEND_INTERVAL_MS) {
    return 0;
  }

  return Math.ceil((EMAIL_CODE_RESEND_INTERVAL_MS - elapsed) / 1_000);
}
